import React, {useEffect, useMemo, useRef, useState} from 'react';
import 'react-chat-elements/dist/main.css';
import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import LoadingSpinner from "../LoadingSpinner";
import MessageList from "./MessageList";
import ChatEditor, {ToolbarButton} from "./ChatEditor";
import createLinkPlugin from "@draft-js-plugins/anchor";
import linkStyles from "./linkStyles.css";
import {BoldButton, ItalicButton, UnderlineButton} from "@draft-js-plugins/buttons";
import {Separator} from "@draft-js-plugins/static-toolbar";
import ErrorBanner from "../ErrorBanner";
import Conversation, {getConversation} from "../../models/Conversation";
import {useUnmount} from "react-use";
import {useAuth} from "../../providers/AuthProvider";


const Typing = () => (
    <div className="typing">
        <div className="typing__dot"></div>
        <div className="typing__dot"></div>
        <div className="typing__dot"></div>
    </div>
)
const sendMessage = async( candidateId, positionId, projectId, content) => {

    try {
        let request;
        let API = `/api/conversations/`
        if(positionId) {
            API += `positions/${positionId}/candidates/${candidateId}/create`
        }else if(projectId && candidateId) {
            API += `projects/${projectId}/team-candidates/${candidateId}/create`
        }else if(candidateId) {
            API += `job-candidates/${candidateId}/create`
        }else {
            return null
        }
        request = TB.request(API, {content});
        let response = await request.post().catch(e => {
            console.warn('ERROR:sendMessage ', e)
        });

        if (request.response.status === 404) {
            return null;
        }

        return Conversation.make({...response.success.data.conversation});
    }catch (e) {
        throw new Error(e);
    }
}

const Chat = (props) => {

    const messagesRef = useRef(null);
    const {auth} = useAuth()
    const [conversationHeight, setConversationHeight] = useState(null);
    const queryClient = useQueryClient();
    const [recipientTyping, setRecipientTyping] = useState(false);
    const [chatChannel, setChatChannel] = useState(null)

    const scrollToLastMessage = (isScrollLast = true, animate = true) => {
        if (!messagesRef.current) {
            return;
        }
        const chatConversation = messagesRef.current;

        let height = chatConversation.scrollHeight;
        if (!isScrollLast) {
            height = height / 2;
        }
        if(!animate) chatConversation.scrollTop = height;

        return $('#conversation-container').animate({ scrollTop: height }, 400);
    }
    const queryParams = {projectId: props.projectId, positionId: props.positionId, candidateId: props.candidate?.id};

    const updateChat = async(updatedConversation) => {
        const newMessageAdded = updatedConversation.messages?.length > conversation.messages?.length;

        await queryClient.invalidateQueries(['getConversation',
            queryParams
        ]);
        if(newMessageAdded) {
            scrollToLastMessage()
        }
    }

    const [currentPage, setCurrentPage] = useState(1);


    const {
        data,
        isLoading,
        error,
        refetch: refetchConversation,
        isRefetching,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
    } = useInfiniteQuery(['getConversation', queryParams],
        getConversation, {
            getNextPageParam: (lastPage, users) => {
                if (lastPage?.current_page && lastPage.current_page < lastPage.last_page) {
                    return lastPage.current_page + 1
                }
                return undefined
            },
            getPreviousPageParam: (firstPage, pages) => {
                if (firstPage?.current_page > 1) {
                    return firstPage.current_page - 1
                }
                return undefined
            },
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            // keepPreviousData: true,
            onSuccess: async ({pages, pageParams: params}) => {
                if ((pages.length === 1 && params[0] === undefined) || (TB.max(TB.values(TB.countBy(params, (page) => page))) > 1)
                ) {

                    queryClient.setQueryData(['getConversation',queryParams], updater => {
                        if (!updater) return undefined;

                        let pages = [...updater.pages];

                        let updated = TB.reduce([...updater.pageParams], (result, value, key) => {
                            let page = value || 1;

                            if (!result[page]) {
                                result[page] = pages[key];
                            }
                            return result;
                        }, {});
                        return {
                            pages: TB.values(updated),
                            pageParams: TB.keys(updated)
                        }
                    })
                    if ((pages.length === 1 && params[0] === undefined)) {
                        return;
                    }
                }

                if (props.onFetchSuccess) props.onFetchSuccess();
            },
        }

    );

    useUnmount(() => {
        if(window.Echo && chatChannel && conversation?.id) {
            window.Echo.leave(`Conversation.${conversation?.id}`)
            setChatChannel(null);
        }
    })

    const [message, setMessage] = useState("");

    const conversation = useMemo(() => {
        if (data?.pages) {

            let lastUpdated = TB.last(data.pages);

            let messages = TB.orderBy(TB.uniqBy(TB.flatten(TB.map(data.pages, 'messages')), 'id'), 'sent_at')

            return Conversation.make({
                ...lastUpdated, messages
            })
        }
    }, [data, isLoading]);

    useEffect(() => {
        console.log('window.Echo', window.Echo)
        if(window.Echo && conversation && !chatChannel) {
            setChatChannel(
                window.Echo.private(`Conversation.${conversation.id}`)
                    .subscribed(data => {
                        console.log("SUBSCRIBED", data)
                    })
                    .listen('.new-message', (data) => {

                        console.log('new-message', data)
                        setRecipientTyping(false)
                        let message = Conversation.addMessageRecipients(data.message, conversation.recipients);
                        let messages = TB.uniqBy([
                            ...(conversation.messages || []),
                                message
                        ], 'id');

                        return updateChat(Conversation.make({
                            ...conversation,
                            messages
                        }));
                    }).listenForWhisper('typing', async (e) => {

                        let shouldScroll = !recipientTyping;

                        await setRecipientTyping(e.isTyping)

                        if (shouldScroll) {
                            scrollToLastMessage()
                        }
                    })
            )
            scrollToLastMessage(true,false);
        }
    }, [conversation]);

    const formRef = useRef(null);
    const editorRef = useRef(null);

    const sendMessageMutation = useMutation(['sendMessage', queryParams],
        (content) =>
            sendMessage(props.candidate?.id, props.positionId, props.projectId ,content), {
            onSuccess: async(updatedConversation) => {
                await updateChat(updatedConversation);
                setMessage("");
                scrollToLastMessage();
            }
    })


    // useEffect(() => {
    //     return () => {
    //         let parentNode = messagesRef.current.parentNode?.getBoundingClientRect?.();
    //
    //         if (parentNode && formRef.current) {
    //             const convoHeight = parentNode.height - formRef.current.getBoundingClientRect().height
    //             console.log('convoHeight', convoHeight)
    //             setConversationHeight(convoHeight);
    //         }
    //     }
    // }, [message?.length]);


    // TODO: finish this component
    // idea was to have max length character count visible in text box
    // need to set max length in backend in order to to work
    const MAX_LENGTH = 250;

    const linkPlugin = createLinkPlugin({
        theme: linkStyles,
        placeholder: 'https://…',
        linkTarget: '_blank',
    });

    const onSave = async(htmlMessage, messageLength, onSuccess) => {
        if(messageLength > MAX_LENGTH) {
            return;
        }
        return new Promise((resolve, reject) => {
            sendMessageMutation.mutate(htmlMessage,{
                onSuccess,
                onError: () => TB.showServerErrorToast(),
                onSettled: () => resolve()
            })
        });
    }

    const handleScroll = event => {
        if (!isLoading && event.currentTarget.scrollTop === 0 && hasNextPage ) {
            return fetchNextPage()
        }
    };

    return  (
        <div className="wripple-chat">

            <div className="chat-container chat">
                <div className="chat__inner">
                    {/*left section of chat area (chat person selection area)*/}
                    {/*{props.discussions && <Discussions activeConversation={active} onSetActive={() => true}/>}*/}

                    {/* right section of chat area (chat conversation area)*/}
                    <div className={TB.mapClass("chat__area-wrapper", {
                        "lg:ml-3": props.discussions
                    })}>
                        {(isLoading || (!conversation && isRefetching)) ? (
                            <div className="chat__not-selected">
                                <div className="flex-col-center" style={{flex: 1}}>
                                    <LoadingSpinner size={"lg"}/>
                                </div>
                            </div>
                        ): (
                            conversation ? (
                                <React.Fragment>
                                    <div className="chat-conversation position-relative"
                                         ref={messagesRef} onScroll={hasNextPage ? handleScroll : undefined} id="conversation-container">

                                        {conversation.messages?.length > 0 ?
                                            <MessageList auth={TB.auth().user()} messages={conversation.messages || []}/> : (
                                                <div className="chat__not-selected">
                                                    <div className="text-center">
                                                        <i className="fa fa-comments fa-4x" aria-hidden="true"/>
                                                    </div>
                                                    {props.discussions ? "No conversation selected yet" : "Send the first message"}
                                                </div>
                                            )}

                                        {recipientTyping && (
                                            <div className="flex justify-center items-center">
                                                <Typing/>
                                            </div>
                                        )}
                                    </div>
                                    <div ref={formRef} className="chat__area-text">
                                        <div className="chat__area-form">
                                            <ChatEditor
                                                placeholder="Type message"
                                                maxLength={MAX_LENGTH}
                                                enableScroll={true}
                                                message={message}
                                                disabled={sendMessageMutation.isLoading}
                                                plugins={[linkPlugin]}
                                                toolbarBtns={[
                                                    BoldButton,
                                                    ItalicButton,
                                                    UnderlineButton,
                                                    Separator,
                                                    // UnorderedListButton,
                                                    linkPlugin.LinkButton,
                                                    (toolProps) => {
                                                        return (
                                                            <ToolbarButton
                                                                {...toolProps}
                                                                className="send-message-btn"
                                                                disabled={toolProps.disabled || !toolProps.messageLength || (toolProps.messageLength > MAX_LENGTH)}
                                                                onClick={async() => {
                                                                    if (toolProps.htmlMessage) {
                                                                        await onSave(toolProps.htmlMessage, toolProps.messageLength, toolProps.reset);
                                                                    }
                                                                }}>
                                                                <p className="mb-0">Send</p>
                                                            </ToolbarButton>
                                                        )
                                                    }
                                                ]}
                                                onHandleReturn={onSave}
                                                onChange={(html, messageLength) => {
                                                    if(messageLength > MAX_LENGTH) {
                                                        return;
                                                    }
                                                    if(message.length !== html.length) {
                                                        chatChannel.whisper('typing', {
                                                            name: auth.firstNamePlusInitial(),
                                                            isTyping: html.length > 0
                                                        });
                                                    }
                                                    setMessage(html)
                                                }}>

                                            </ChatEditor>
                                        </div>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <div className="chat__not-selected">
                                    <ErrorBanner title="Something went wrong" message="Failed to load messages" onRetry={() => refetchConversation()}/>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

        </div>
    )
}

export default Chat;